import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import dayjs from "dayjs";
import Overlay from "components/Overlay/Overlay";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Status → presentation mapping for the verification results.
const STATUS_META = {
  synced: {
    label: "Synced",
    color: "success",
    icon: CloudDoneRoundedIcon,
  },
  outdated: {
    label: "Out of date",
    color: "warning",
    icon: WarningAmberRoundedIcon,
  },
  missing: {
    label: "Not on Outlook",
    color: "error",
    icon: CloudOffRoundedIcon,
  },
  missing_on_outlook: {
    label: "Deleted on Outlook",
    color: "error",
    icon: CloudOffRoundedIcon,
  },
  unverifiable: {
    label: "Unverifiable",
    color: "info",
    icon: WarningAmberRoundedIcon,
  },
  not_eligible: {
    label: "Not eligible",
    color: "grey",
    icon: CloudOffRoundedIcon,
  },
};

// Statuses that the Sync button will fix.
const NEEDS_SYNC_STATUSES = new Set([
  "outdated",
  "missing",
  "missing_on_outlook",
]);

// Multi-assignment syncs loop client-side ONE assignment per request
// (2026-09-22, per Amine — was 5). One at a time gives per-item progress,
// isolates failures to a single assignment (a hiccup can't take down four
// others), and lets the progress panel list every failure with its exact
// reason the moment it happens. Each request stays ~1-2s — far under any
// proxy timeout.
const SYNC_CHUNK_SIZE = 1;

/**
 * Calendar Sync Verification overlay.
 *
 * Opened from the Calendar screen ("Verify Outlook Sync" button). Two steps:
 *   1. CHECK — pick one or more months and verify every eligible assignment's
 *      Outlook event (exists? up to date?). Read-only.
 *   2. SYNC  — one click to create/refresh every assignment flagged as
 *      missing, deleted, or out of date.
 *
 * Follows the standard CE overlay shell: dimmer → rounded panel → header
 * (back chevron + title + close) → scrollable body on `background.alt`.
 */
const CalendarSyncOverlay = () => {
  usePageTitle("Verify Outlook Sync");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { setOverlayName, showSnackbar } = useStateContext();
  const { currentUser } = useAuth();
  // Outlook sync management is admin-only — the Calendar button is hidden for
  // non-admins and the server enforces it too; this guards the overlay itself.
  const isAdmin = currentUser?.Role === "Admin";

  const closeOverlay = () => setOverlayName("");

  // Month/year selection — defaults to the CURRENT month.
  const [selectedMonths, setSelectedMonths] = useState(() => [
    MONTHS[dayjs().month()],
  ]);
  const [selectedYear, setSelectedYear] = useState(() =>
    String(dayjs().year()),
  );

  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [hasChecked, setHasChecked] = useState(false);

  // Selection / per-card sync state (2026-09-21): long-press a card to enter
  // select mode, then tap cards to toggle. Individual syncs track their own
  // spinner so one card never locks the whole list.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [syncingIds, setSyncingIds] = useState([]);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  // Chunked-sync progress (2026-09-22, per Amine): multi-assignment syncs
  // loop in small chunks and show a live overlay — processed / left, the
  // assignments currently in flight, running stats, and a Stop button —
  // instead of one long request that ends in a timeout with no feedback.
  const [progress, setProgress] = useState(null);
  const cancelSyncRef = useRef(false);
  // id → verification row, for human-readable labels (#projectNumber).
  const resultRowsRef = useRef(new Map());
  useEffect(() => {
    resultRowsRef.current = new Map(results.map((row) => [row.serviceId, row]));
  }, [results]);

  const yearOptions = useMemo(() => {
    const current = dayjs().year();
    return [current - 2, current - 1, current, current + 1].map(String);
  }, []);

  // Services the Sync button will process.
  const servicesToSync = useMemo(
    () => results.filter((row) => NEEDS_SYNC_STATUSES.has(row.status)),
    [results],
  );

  const handleCheck = useCallback(async () => {
    if (selectedMonths.length === 0) {
      showSnackbar("Select at least one month to check.", theme, "warning");
      return;
    }

    setChecking(true);
    try {
      const response = await api.post("/calendar/verify", {
        months: selectedMonths,
        year: selectedYear,
      });
      const data = response.data || {};
      setSummary(data.summary || null);
      setResults(Array.isArray(data.services) ? data.services : []);
      setHasChecked(true);
    } catch (error) {
      // 504 = the proxy gave up waiting while the server keeps working — a
      // big month can legitimately take a while (2026-09-22).
      if (error?.response?.status === 504) {
        showSnackbar(
          "The check is taking longer than the request timeout — try fewer months, or check again in a minute.",
          theme,
          "warning",
        );
      } else {
        showSnackbar(
          error?.response?.data?.error || "Failed to verify calendar sync",
          theme,
          "error",
        );
      }
    } finally {
      setChecking(false);
    }
  }, [selectedMonths, selectedYear, showSnackbar, theme]);

  // Shared sync runner — used by "Sync all", "Sync selected", and the
  // per-card sync buttons.
  //
  // Single assignment (per-card ↻): lightweight — one small request with a
  // per-card spinner. Multiple assignments (or a retry): CHUNKED loop with a
  // live progress overlay (2026-09-22, per Amine) showing processed / left,
  // the assignment in flight, running totals, and — crucially — every
  // FAILURE with its reason as it happens. Assignments already synced stay
  // synced, and failed ones can be retried from the panel.
  const syncServiceIds = useCallback(
    async (serviceIds, { individual = false, useProgress = false } = {}) => {
      const ids = [...new Set((serviceIds || []).filter(Boolean))];
      if (ids.length === 0) return;

      const labelFor = (id) => {
        const row = resultRowsRef.current.get(id);
        return row?.projectNumber ? `#${row.projectNumber}` : `#${id}`;
      };

      // --- Single assignment: lightweight per-card path ---
      // (skipped when useProgress is set — the retry flow keeps the panel so
      // a retried failure's reason stays visible even for one item)
      if (ids.length === 1 && !useProgress) {
        if (individual) {
          setSyncingIds(ids);
        } else {
          setSyncing(true);
        }
        try {
          const response = await api.post("/calendar/syncBatch", {
            serviceIds: ids,
          });
          const result = response.data?.summary || {};
          const details = response.data?.details || {};
          const failureReason = details.failed?.[0]?.error;

          // Everything skipped (e.g. Outlook disabled outside production) —
          // surface the server's reason instead of a bare "0 synced".
          if (
            (result.successful || 0) === 0 &&
            (result.skipped || 0) === ids.length
          ) {
            showSnackbar(
              response.data?.message ||
                "Nothing was synced — Outlook sync is disabled outside production.",
              theme,
              "warning",
            );
          } else if ((result.failed || 0) > 0) {
            // Never leave the user guessing WHY — show the server's reason.
            showSnackbar(
              `Outlook sync failed — ${failureReason || "see the Logs Viewer for details"}`,
              theme,
              "error",
            );
          } else {
            showSnackbar(
              `Outlook sync complete — ${result.successful || 0} synced${
                result.failed ? `, ${result.failed} failed` : ""
              }${result.skipped ? `, ${result.skipped} skipped` : ""}.`,
              theme,
              result.failed ? "warning" : "success",
            );
          }

          // Re-check so the list reflects the new state, then leave select mode.
          await handleCheck();
          setSelectMode(false);
          setSelectedIds([]);
        } catch (error) {
          showSnackbar(
            error?.response?.data?.error || "Failed to sync assignments",
            theme,
            "error",
          );
        } finally {
          if (individual) setSyncingIds([]);
          else setSyncing(false);
        }
        return;
      }

      // --- Multiple assignments (or retry): chunked loop + progress overlay ---
      cancelSyncRef.current = false;
      setSyncing(true);
      const totals = { successful: 0, failed: 0, skipped: 0 };
      // Per-item failure details from the server ({ serviceId, error }) —
      // shown live in the panel so failures are never a black box.
      const failedItems = [];
      let processed = 0;
      let abortMessage = null;

      setProgress({
        phase: "running",
        stopping: false,
        total: ids.length,
        processed: 0,
        currentLabels: [],
        failedItems: [],
        ...totals,
      });

      for (let offset = 0; offset < ids.length; offset += SYNC_CHUNK_SIZE) {
        if (cancelSyncRef.current) break;
        const chunk = ids.slice(offset, offset + SYNC_CHUNK_SIZE);
        setProgress((prev) =>
          prev && { ...prev, currentLabels: chunk.map(labelFor) },
        );

        let response = null;
        let requestError = null;
        try {
          response = await api.post("/calendar/syncBatch", {
            serviceIds: chunk,
          });
        } catch (error) {
          // Expired Azure credentials break every request — stop with the
          // server's clear message instead of grinding through the rest.
          if (error?.response?.status === 502) {
            abortMessage =
              error.response?.data?.error ||
              "Outlook/Graph credentials have expired.";
            break;
          }
          // One retry for a transient blip (network hiccup / proxy reset).
          try {
            response = await api.post("/calendar/syncBatch", {
              serviceIds: chunk,
            });
          } catch (retryError) {
            requestError = retryError;
          }
        }

        if (requestError) {
          // The request never landed — record these items as failed with the
          // network-level reason and KEEP GOING (one bad request must not
          // abandon the rest of the run).
          const message =
            requestError?.response?.data?.error ||
            requestError?.message ||
            "The sync request failed.";
          chunk.forEach((id) => {
            failedItems.push({ serviceId: id, label: labelFor(id), error: message });
            totals.failed += 1;
          });
          processed += chunk.length;
          const errorSnapshot = {
            processed,
            ...totals,
            failedItems: [...failedItems],
          };
          setProgress((prev) =>
            prev && { ...prev, currentLabels: [], ...errorSnapshot },
          );
          continue;
        }

        const result = response?.data?.summary || {};
        const details = response?.data?.details || {};
        // Collect the server's per-item failure reasons (serviceId + error).
        if (Array.isArray(details.failed)) {
          details.failed.forEach((item) => {
            const id = Number(item?.serviceId);
            failedItems.push({
              serviceId: id,
              label: labelFor(id),
              error: item?.error || "Unknown error",
            });
          });
        }
        totals.successful += result.successful || 0;
        totals.failed += result.failed || 0;
        totals.skipped += result.skipped || 0;
        processed += chunk.length;
        // Snapshot the counters — the state updater may run after the next
        // chunk starts, so it must not read the mutable loop variables.
        const snapshot = {
          processed,
          ...totals,
          failedItems: [...failedItems],
        };
        setProgress((prev) =>
          prev && { ...prev, currentLabels: [], ...snapshot },
        );
      }

      const stopped = !abortMessage && cancelSyncRef.current;
      setProgress((prev) =>
        prev && {
          ...prev,
          currentLabels: [],
          stopping: false,
          processed,
          ...totals,
          failedItems: [...failedItems],
          phase: abortMessage ? "error" : stopped ? "stopped" : "done",
          errorMessage: abortMessage,
        },
      );
      setSyncing(false);
    },
    [showSnackbar, theme, handleCheck],
  );

  const handleSync = useCallback(async () => {
    if (servicesToSync.length === 0) return;
    await syncServiceIds(servicesToSync.map((row) => row.serviceId));
  }, [syncServiceIds, servicesToSync]);

  // -------------------------------------------------------------------
  // Per-card sync + long-press selection (2026-09-21, per Amine).
  // -------------------------------------------------------------------

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Cancel any pending long-press when the overlay closes.
  useEffect(() => clearLongPressTimer, []);

  const handleCardPointerDown = (serviceId) => {
    if (selectMode) return; // already selecting — taps toggle instead
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setSelectMode(true);
      setSelectedIds([serviceId]);
    }, 500);
  };

  const handleCardPointerUp = () => {
    clearLongPressTimer();
  };

  const toggleSelection = (serviceId) => {
    setSelectedIds((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId],
    );
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const handleCardClick = (serviceId) => {
    // A long-press also fires a click on release — swallow that one.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (selectMode) {
      toggleSelection(serviceId);
    }
  };

  const handleSyncSingle = useCallback(
    (serviceId) => syncServiceIds([serviceId], { individual: true }),
    [syncServiceIds],
  );

  const handleSyncSelected = useCallback(
    () => syncServiceIds(selectedIds, { individual: true }),
    [syncServiceIds, selectedIds],
  );

  // --- Progress overlay actions (multi-assignment syncs) ---
  const handleProgressStop = () => {
    cancelSyncRef.current = true;
    setProgress((prev) => prev && { ...prev, stopping: true });
  };

  // Copy every failure (label + server reason) so the list can be pasted into
  // a ticket/email for follow-up (2026-09-22, per Amine — "left in the dark").
  const handleProgressCopyFailures = useCallback(() => {
    const items = progress?.failedItems || [];
    if (items.length === 0) return;
    const text =
      "Outlook sync failures:\n" +
      items.map((item) => `${item.label} — ${item.error}`).join("\n");
    try {
      navigator.clipboard.writeText(text);
      showSnackbar("Failure details copied to the clipboard", theme, "success");
    } catch (_) {
      showSnackbar("Could not copy — use the list in the panel", theme, "warning");
    }
  }, [progress, showSnackbar, theme]);

  // Re-run just the failed items through the same panel so the outcome of
  // each retry stays visible.
  const handleProgressRetryFailed = useCallback(() => {
    const failedIds = [
      ...new Set(
        (progress?.failedItems || [])
          .map((item) => item.serviceId)
          .filter((id) => Number.isInteger(id) && id > 0),
      ),
    ];
    if (failedIds.length === 0) return;
    setProgress(null);
    syncServiceIds(failedIds, { useProgress: true });
  }, [progress, syncServiceIds]);

  const handleProgressDone = useCallback(async () => {
    const finished = progress;
    setProgress(null);
    cancelSyncRef.current = false;
    if (finished) {
      const remaining = Math.max(0, finished.total - finished.processed);
      const parts = [`${finished.successful} synced`];
      if (finished.failed) parts.push(`${finished.failed} failed`);
      if (finished.skipped) parts.push(`${finished.skipped} skipped`);
      if (finished.phase === "stopped" && remaining) {
        parts.push(`${remaining} left`);
      }
      showSnackbar(
        `Outlook sync — ${parts.join(", ")}.`,
        theme,
        finished.failed || finished.phase === "error" ? "warning" : "success",
      );
    }
    // Refresh the list so cards show their new status, then leave select mode.
    await handleCheck();
    setSelectMode(false);
    setSelectedIds([]);
  }, [progress, showSnackbar, theme, handleCheck]);

  const toggleMonth = (month) => {
    setSelectedMonths((prev) =>
      prev.includes(month)
        ? prev.filter((m) => m !== month)
        : [...prev, month],
    );
  };

  // Visible field borders (same convention as the Logs Viewer).
  const selectFieldSx = {
    backgroundColor: theme.palette.background.default,
    borderRadius: "10px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.border2,
      borderWidth: 1,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  };

  const primaryButtonSx = {
    minWidth: 200,
    borderRadius: "12px",
    py: 1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    fontWeight: "bold",
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.buttonHoverSelected,
      color: theme.palette.background.default,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.primary.border2,
      color: theme.palette.background.default,
    },
  };

  const renderStatusChip = (status) => {
    const meta = STATUS_META[status] || STATUS_META.unverifiable;
    const Icon = meta.icon;
    const paletteColor =
      meta.color === "grey"
        ? theme.palette.grey[500]
        : theme.palette[meta.color]?.main || theme.palette.primary.main;
    return (
      <Chip
        size="small"
        icon={<Icon sx={{ fontSize: 15 }} />}
        label={meta.label}
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 600,
          color: paletteColor,
          borderColor: paletteColor,
          backgroundColor: "transparent",
          "& .MuiChip-icon": { color: paletteColor },
        }}
        variant="outlined"
      />
    );
  };

  return (
    <Overlay
      id="calendar-sync"
      open={true}
      onClose={closeOverlay}
      title="Verify Outlook Sync"
    >

        {/* Body */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            overflowY: "auto",
          }}
        >
          {!isAdmin ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                border: `1px dashed ${theme.palette.primary.border2}`,
                borderRadius: "12px",
              }}
            >
              <Typography color="text.secondary" fontWeight={600}>
                Admins only
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Outlook sync management is restricted to administrators.
              </Typography>
            </Box>
          ) : (
            <>
          {/* Step 1 — month selection + check */}
          <Box
            sx={{
              bgcolor: theme.palette.background.default,
              border: `1px solid ${theme.palette.primary.border2}`,
              borderRadius: "12px",
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
              1. Choose the months to check
            </Typography>

            <Stack
              direction="row"
              spacing={0.75}
              sx={{ flexWrap: "wrap", gap: 0.75, mb: 1.5 }}
            >
              {MONTHS.map((month) => {
                const selected = selectedMonths.includes(month);
                return (
                  <Chip
                    key={month}
                    size="small"
                    label={month}
                    onClick={() => toggleMonth(month)}
                    sx={{
                      color: selected
                        ? theme.palette.background.default
                        : theme.palette.primary.main,
                      backgroundColor: selected
                        ? theme.palette.primary.main
                        : "transparent",
                      border: `1px solid ${theme.palette.primary.main}`,
                      fontWeight: selected ? "bold" : "normal",
                      "&:hover": {
                        backgroundColor: selected
                          ? theme.palette.primary.main
                          : theme.palette.primary.buttonHoverSelected,
                        color: theme.palette.background.default,
                      },
                    }}
                  />
                );
              })}
            </Stack>

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={1.5}
              alignItems={isMobile ? "stretch" : "center"}
            >
              <Select
                size="small"
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
                sx={{ minWidth: 120, ...selectFieldSx }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>

              <Button
                onClick={handleCheck}
                disabled={checking || syncing}
                startIcon={
                  checking ? (
                    <CircularProgress
                      size={16}
                      sx={{ color: theme.palette.background.default }}
                    />
                  ) : (
                    <SyncRoundedIcon />
                  )
                }
                sx={primaryButtonSx}
              >
                {checking ? "Checking…" : "Check Sync Status"}
              </Button>
            </Stack>

            {checking && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
          </Box>

          {/* Step 2 — results + sync */}
          {hasChecked && (
            <>
              {summary && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  {[
                    ["Total", summary.total, theme.palette.primary.text],
                    ["Synced", summary.synced, theme.palette.success.main],
                    ["Out of date", summary.outdated, theme.palette.warning.main],
                    [
                      "Not on Outlook",
                      summary.missing + summary.missing_on_outlook,
                      theme.palette.error.main,
                    ],
                    [
                      "Unverifiable",
                      summary.unverifiable,
                      theme.palette.info.main,
                    ],
                  ].map(([label, value, color]) => (
                    <Chip
                      key={label}
                      size="small"
                      label={`${label} (${value})`}
                      sx={{
                        color,
                        border: `1px solid ${color}`,
                        backgroundColor: "transparent",
                        fontWeight: 600,
                      }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}

              {servicesToSync.length > 0 && (
                <Box
                  sx={{
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: "12px",
                    p: 1.5,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                    2. Sync the{" "}
                    <strong>{servicesToSync.length}</strong> assignment
                    {servicesToSync.length === 1 ? "" : "s"} that need
                    attention. This creates or refreshes their Outlook events.
                  </Typography>
                  <Button
                    onClick={handleSync}
                    disabled={syncing || checking}
                    startIcon={
                      syncing ? (
                        <CircularProgress
                          size={16}
                          sx={{ color: theme.palette.background.default }}
                        />
                      ) : (
                        <SyncRoundedIcon />
                      )
                    }
                    sx={primaryButtonSx}
                  >
                    {syncing
                      ? "Syncing…"
                      : `Sync ${servicesToSync.length} assignment${
                          servicesToSync.length === 1 ? "" : "s"
                        }`}
                  </Button>
                </Box>
              )}

              {/* Up-to-date panel — only when something was actually verified.
                  In non-production every row is "Unverifiable", so this must
                  NOT claim everything is in sync (2026-09-21). */}
              {hasChecked &&
                servicesToSync.length === 0 &&
                summary &&
                (summary.synced > 0 || summary.unverifiable === 0) && (
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.success.main}`,
                      borderRadius: "12px",
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Everything is up to date on Outlook 🎉
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      All eligible assignments for the selected months match
                      their Outlook events.
                    </Typography>
                  </Box>
                )}

              {/* Non-production notice — Graph is disabled, nothing verifiable */}
              {hasChecked &&
                summary &&
                summary.unverifiable > 0 &&
                summary.synced === 0 &&
                summary.outdated === 0 &&
                summary.missing === 0 &&
                summary.missing_on_outlook === 0 && (
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.info.main}`,
                      borderRadius: "12px",
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Cannot verify from this environment
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Outlook sync is disabled outside production, so the
                      calendar could not be checked. Run the verification on
                      production to see real results.
                    </Typography>
                  </Box>
                )}

              <Divider sx={{ my: 1.5 }} />

              {/* Select-mode toolbar — appears after a long-press on a card */}
              {selectMode && (
                <Box
                  sx={{
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                    p: 1.25,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" fontWeight={700} sx={{ mr: "auto" }}>
                    {selectedIds.length} selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      setSelectedIds(
                        servicesToSync.map((row) => row.serviceId),
                      )
                    }
                    disabled={servicesToSync.length === 0}
                    sx={{
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: "10px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHoverSelected,
                        color: theme.palette.background.default,
                      },
                    }}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedIds([])}
                    disabled={selectedIds.length === 0}
                    sx={{
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.border2}`,
                      borderRadius: "10px",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    onClick={exitSelectMode}
                    sx={{
                      color: theme.palette.primary.text,
                      border: `1px solid ${theme.palette.primary.border2}`,
                      borderRadius: "10px",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    onClick={handleSyncSelected}
                    disabled={selectedIds.length === 0 || syncing || checking}
                    startIcon={
                      syncing ? (
                        <CircularProgress
                          size={14}
                          sx={{ color: theme.palette.background.default }}
                        />
                      ) : (
                        <SyncRoundedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHoverSelected,
                        color: theme.palette.background.default,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.primary.border2,
                        color: theme.palette.background.default,
                      },
                    }}
                  >
                    Sync selected ({selectedIds.length})
                  </Button>
                </Box>
              )}

              {/* Per-assignment results */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Assignments checked ({results.length})
                </Typography>
                {!selectMode && servicesToSync.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    · Long-press a card to select several
                  </Typography>
                )}
              </Box>

              {results.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 5,
                    border: `1px dashed ${theme.palette.primary.border2}`,
                    borderRadius: "12px",
                  }}
                >
                  <Typography color="text.secondary">
                    No eligible assignments in the selected months
                  </Typography>
                </Box>
              ) : (
                results.map((row) => {
                  const needsSync = NEEDS_SYNC_STATUSES.has(row.status);
                  const isSelected = selectedIds.includes(row.serviceId);
                  const isSyncingThis = syncingIds.includes(row.serviceId);
                  const accentColor =
                    STATUS_META[row.status]?.color === "grey"
                      ? theme.palette.grey[500]
                      : theme.palette[STATUS_META[row.status]?.color]?.main ||
                        theme.palette.primary.main;

                  return (
                    <Box
                      key={row.serviceId}
                      onPointerDown={() => handleCardPointerDown(row.serviceId)}
                      onPointerUp={handleCardPointerUp}
                      onPointerLeave={handleCardPointerUp}
                      onPointerCancel={handleCardPointerUp}
                      onClick={() => handleCardClick(row.serviceId)}
                      sx={{
                        bgcolor: theme.palette.background.default,
                        border: `1px solid ${
                          isSelected
                            ? theme.palette.primary.main
                            : theme.palette.primary.border2
                        }`,
                        borderLeft: `4px solid ${accentColor}`,
                        borderRadius: "10px",
                        p: 1.25,
                        mb: 1,
                        cursor: selectMode ? "pointer" : "default",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        touchAction: "manipulation",
                      }}
                    >
                      {selectMode && (
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleSelection(row.serviceId)}
                          onClick={(event) => event.stopPropagation()}
                          sx={{ p: 0.25, mt: 0.25 }}
                        />
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 0.5,
                          }}
                        >
                          {renderStatusChip(row.status)}
                          <Typography variant="body2" fontWeight={700}>
                            {row.projectNumber
                              ? `#${row.projectNumber}`
                              : `Service #${row.serviceId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.date
                              ? dayjs(row.date).format("M/D/YYYY")
                              : "—"}
                            {row.timeStart
                              ? ` · ${String(row.timeStart).slice(0, 5)}`
                              : ""}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {[row.client, row.linguist, row.reason]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>

                      {/* Per-card sync — only for cards that need attention */}
                      {!selectMode && needsSync && (
                        <Tooltip title="Sync this assignment">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isSyncingThis || syncing || checking}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSyncSingle(row.serviceId);
                              }}
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.primary.buttonHoverSelected,
                                  color: theme.palette.background.default,
                                },
                              }}
                            >
                              {isSyncingThis ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SyncRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })
              )}
            </>
          )}
            </>
          )}
        </Box>

      {/* Progress overlay — multi-assignment syncs run in chunks with live
          feedback: processed / left, the assignments in flight, running
          stats, and a Stop button (2026-09-22, per Amine). */}
      {progress && (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.65)",
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: theme.palette.background.default,
              width: "100%",
              maxWidth: 560,
              borderRadius: "1.5rem",
              p: "1.25rem 1.5rem",
              boxShadow: theme.shadows[8],
            }}
          >
            {/* Header */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              {progress.phase === "running" ? (
                <SyncRoundedIcon
                  sx={{
                    color: theme.palette.primary.main,
                    animation: "ceSyncSpin 1.4s linear infinite",
                    "@keyframes ceSyncSpin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              ) : progress.phase === "done" ? (
                <CloudDoneRoundedIcon
                  sx={{ color: theme.palette.success.main }}
                />
              ) : (
                <WarningAmberRoundedIcon
                  sx={{
                    color:
                      progress.phase === "error"
                        ? theme.palette.error.main
                        : theme.palette.warning.main,
                  }}
                />
              )}
              <Typography variant="h5" fontWeight="bold">
                {progress.phase === "running"
                  ? progress.stopping
                    ? "Stopping…"
                    : "Syncing to Outlook"
                  : progress.phase === "done"
                    ? "Sync complete"
                    : progress.phase === "stopped"
                      ? "Sync stopped"
                      : "Sync interrupted"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto", fontWeight: 600 }}
              >
                {progress.processed} / {progress.total}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={
                progress.total
                  ? Math.round((progress.processed / progress.total) * 100)
                  : 0
              }
              sx={{
                borderRadius: 1,
                mb: 1.5,
                height: 8,
                backgroundColor: theme.palette.background.alt,
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    progress.phase === "error"
                      ? theme.palette.error.main
                      : progress.phase === "stopped"
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
                },
              }}
            />

            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{progress.processed}</strong> processed ·{" "}
              <strong>
                {Math.max(0, progress.total - progress.processed)}
              </strong>{" "}
              left
            </Typography>

            {/* In-flight assignments — what is being synced right now */}
            {progress.phase === "running" && (
              <Box sx={{ mb: 1.5, minHeight: 30 }}>
                {progress.currentLabels.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ flexWrap: "wrap", gap: 0.75 }}
                  >
                    {progress.currentLabels.map((label, index) => (
                      <Chip
                        key={`${label}-${index}`}
                        size="small"
                        label={label}
                        sx={{
                          border: `1px solid ${theme.palette.primary.main}`,
                          color: theme.palette.primary.main,
                          backgroundColor: "transparent",
                          fontWeight: 600,
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Preparing next batch…
                  </Typography>
                )}
              </Box>
            )}

            {/* Running stats */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                size="small"
                label={`Synced (${progress.successful})`}
                sx={{
                  color: theme.palette.success.main,
                  border: `1px solid ${theme.palette.success.main}`,
                  backgroundColor: "transparent",
                  fontWeight: 600,
                }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Failed (${progress.failed})`}
                sx={{
                  color: theme.palette.error.main,
                  border: `1px solid ${theme.palette.error.main}`,
                  backgroundColor: "transparent",
                  fontWeight: 600,
                }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Skipped (${progress.skipped})`}
                sx={{
                  color: theme.palette.warning.main,
                  border: `1px solid ${theme.palette.warning.main}`,
                  backgroundColor: "transparent",
                  fontWeight: 600,
                }}
                variant="outlined"
              />
            </Box>

            {/* Per-item failures — label + the server's exact reason, added
                the moment each one fails so nothing is a black box. */}
            {progress.failedItems.length > 0 && (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.error.main}`,
                  borderRadius: "10px",
                  mb: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    bgcolor: "rgba(211, 47, 47, 0.10)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.error.main, fontWeight: 700 }}
                  >
                    Failures ({progress.failedItems.length})
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    reasons from the server
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 170, overflowY: "auto", p: 1.25 }}>
                  {progress.failedItems.map((item) => (
                    <Box
                      key={item.serviceId}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        mb: 0.75,
                        "&:last-of-type": { mb: 0 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {item.error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {progress.phase === "error" && progress.errorMessage && (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.error.main}`,
                  borderRadius: "10px",
                  p: 1,
                  mb: 2,
                }}
              >
                <Typography variant="caption" color="error">
                  {progress.errorMessage}
                </Typography>
              </Box>
            )}

            {progress.phase === "stopped" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                Assignments already synced stay synced. You can sync the
                remaining ones any time.
              </Typography>
            )}

            {/* Actions */}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
            >
              {progress.phase === "running" ? (
                <Button
                  onClick={handleProgressStop}
                  disabled={progress.stopping}
                  sx={{
                    minWidth: 140,
                    borderRadius: "12px",
                    py: 1,
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.border2}`,
                    textTransform: "none",
                    fontWeight: "bold",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  {progress.stopping ? "Stopping…" : "Stop"}
                </Button>
              ) : (
                <>
                  {progress.failedItems.length > 0 && (
                    <Button
                      onClick={handleProgressCopyFailures}
                      sx={{
                        borderRadius: "12px",
                        py: 1,
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary.border2}`,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                        },
                      }}
                    >
                      Copy failures
                    </Button>
                  )}
                  {progress.failedItems.length > 0 && (
                    <Button
                      onClick={handleProgressRetryFailed}
                      sx={{
                        borderRadius: "12px",
                        py: 1,
                        color: theme.palette.error.main,
                        border: `1px solid ${theme.palette.error.main}`,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: theme.palette.error.main,
                          color: theme.palette.background.default,
                        },
                      }}
                    >
                      Retry failed ({progress.failedItems.length})
                    </Button>
                  )}
                  <Button onClick={handleProgressDone} sx={primaryButtonSx}>
                    Done
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Overlay>
  );
};

export default CalendarSyncOverlay;
